import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  Checkbox,
  ContentSection,
  Fieldset,
  FormSection,
  Layout,
} from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { CountryCodesEnum, frenchTerritoriesOptions } from '../../../../resources/base/models/country';
import { CustomerDetailsSection } from '../../../../resources/base/models/enums';
import {
  CitiesAndZipCodesSuggestion,
  localitiesWithZipCodes,
  zipCodesForLocality,
} from '../../../../services/api/address/localitiesAndZipCodes';
import {
  createStakeholderContact,
  removeStakeholderContact,
  updateStakeholderContact,
} from '../../../../services/api/miles/miles-customer-services';
import { getQuoteStakeholderContacts, unlinkStakeholderContact } from '../../../../services/api/miles/miles-services';
import { updateDataStorageNew } from '../../../../services/api/updateDataStorage/updateDataStorage';
import { getTransactionId } from '../../../../services/common/data';
import { assertDefined } from '../../../../services/common/miscUtils';
import { staticSelector } from '../../../../services/redux';
import {
  creditApplicationSelectors,
  setFormAsNotSubmitted,
} from '../../../../services/redux/features/creditApplication.slice';
import {
  customerSelectors,
  setIsSignatoryFormActive,
  setRemoveStakeholderContact,
  setShouldLinkDirectorStakeholder,
  setStakeholderContact,
} from '../../../../services/redux/features/customer.slice';
import {
  selectCreditApplicationStakeholdersFromDataStorage,
  selectMilesCustomerIdFromDataStorage,
  selectQuoteIdFromDataStorage,
  setDataStorageNew,
} from '../../../../services/redux/features/dataStorage.redux';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { quoteSelectors, setStakeholderContacts } from '../../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { validateEmailTls } from '../../utils/checkTLSUtil';
import {
  CeoBirthCountry,
  CeoBirthName,
  CeoBirthPlaceCity,
  CeoBirthPlaceZip,
  CeoDateOfBirth,
  CeoFirstName,
  CeoLastName,
  CeoNationality,
  CountryCodeMobilePhone,
  Email,
  Gender,
  MobilePhone,
} from '../../utils/fieldsBusinessCustomer';
import { formatCityNameLettersAndSpaceOnly } from '../../utils/formatCityString';
import { CustomerDetailsFormGroupForms, CustomerDetailsFormGroupType, useFormSectionHelpers } from '../utils';
import { ExecutiveInfoDataFormType, getStakeholderInfoDataFormInitialValues } from './initialValues';
import { executiveInfoDataFormCreateRequestMapper, executiveInfoDataFormUpdateRequestMapper } from './requestMapper';
import { StakeholderRemovalModal } from './StakeholderRemovalModal';
import { getExecutiveInfoDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'business'>;
  formGroup: CustomerDetailsFormGroupType;
}

const initialStakeholderValues = getStakeholderInfoDataFormInitialValues();

export const ExecutiveInfoDataForm: FunctionComponent<Props> = ({ formName, formGroup }) => {
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<ExecutiveInfoDataFormType>>();
  const isSignatoryFormActive = useSelector(customerSelectors.selectIsSignatoryFormActive);
  const stakeholderContacts = useSelector(customerSelectors.selectStakeholderContacts);
  const creditApplicationStakeholders = staticSelector(selectCreditApplicationStakeholdersFromDataStorage);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const milesCustomerId =
    useSelector(customerSelectors.selectCustomerId) || staticSelector(selectMilesCustomerIdFromDataStorage);
  const executiveContactData = useMemo(() => stakeholderContacts.DIRECTOR, [stakeholderContacts]);

  useEffect(() => {
    setValidationSchema(getExecutiveInfoDataFormValidationSchema(t));
  }, [t]);

  useEffect(() => {
    // When checkox is changed user is forced to submit the form again
    dispatch(setFormAsNotSubmitted(CustomerDetailsSection.EXECUTIVE_INFO));
  }, [dispatch, isSignatoryFormActive]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    formGroup,
    formName
  );
  const onExecutiveRemoval = async () => {
    try {
      dispatch(loadingActions.setRenderingLocalSpinner(true));
      await removeStakeholderContact(
        assertDefined(milesCustomerId),
        assertDefined(executiveContactData?.milesStakeholderContactId)
      );
      dispatch(setRemoveStakeholderContact(executiveContactData!));
      dispatch(loadingActions.setRenderingLocalSpinner(false));
    } catch (e) {
      dispatch(loadingActions.setRenderingLocalSpinner(false));
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onSubmit = async (values: ExecutiveInfoDataFormType) => {
    const milesEmailId = executiveContactData?.milesEmailId;
    const milesMobilePhoneNumberId = executiveContactData?.milesMobilePhoneNumberId;

    try {
      // Existing executive updated
      if (executiveContactData?.milesContactId) {
        const response = await updateStakeholderContact(
          assertDefined(milesCustomerId),
          assertDefined(executiveContactData.milesStakeholderContactId),
          executiveInfoDataFormUpdateRequestMapper(values, milesMobilePhoneNumberId, milesEmailId)
        );

        const responseContact = response.stakeholderContacts?.at(0);
        if (responseContact) {
          dispatch(
            setStakeholderContact({
              role: 'DIRECTOR' as any,
              milesContactId: responseContact.milesContactId,
              milesStakeholderContactId: executiveContactData.milesStakeholderContactId,
              milesEmailId: responseContact.contactData?.emails?.at(0)?.milesEmailId,
              milesMobilePhoneNumberId: responseContact.contactData?.milesMobilePhoneNumberId,
              formData: values,
              signatoryStakeholderContactId:
                responseContact.stakeholderContact?.milesStakeholderContactType === 'SIGNATORY'
                  ? responseContact.stakeholderContact?.milesStakeholderContactId
                  : executiveContactData.signatoryStakeholderContactId ||
                    executiveContactData.milesStakeholderContactId,
            })
          );
        }

        // New executive created
      } else {
        const response = await createStakeholderContact(
          assertDefined(milesCustomerId),
          executiveInfoDataFormCreateRequestMapper(values)
        );

        dispatch(
          setStakeholderContact({
            role: 'DIRECTOR',
            milesContactId: response.milesContactId,
            milesStakeholderContactId: response.stakeholderContacts?.find(
              (sc) => sc.milesStakeholderContactType === 'DIRECTOR'
            )?.milesStakeholderContactId,
            milesEmailId: response.contactData?.emails?.at(0)?.milesEmailId,
            milesMobilePhoneNumberId: response.contactData?.milesMobilePhoneNumberId,
            formData: values,
            signatoryStakeholderContactId: response.stakeholderContacts?.find(
              (sc) => sc.milesStakeholderContactType === 'SIGNATORY'
            )?.milesStakeholderContactId,
          })
        );
        dispatch(setShouldLinkDirectorStakeholder(true));
      }

      if (executiveContactData?.milesContactId) {
        await updateDataStorageNew(getTransactionId(), {
          custom: {
            creditApplicationStakeholders: {
              ...creditApplicationStakeholders,
              directorContactId: executiveContactData.milesContactId,
            },
          },
        }).then((updatedDataStorageItem) => dispatch(setDataStorageNew(updatedDataStorageItem.custom)));
      }
    } catch {
      navigate.error();
      return;
    } finally {
      dispatch(loadingActions.setLoading(false));
    }

    onFormSubmitSuccess();
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:executiveInformation')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={executiveContactData?.formData || initialStakeholderValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent onFormInitialValuesChange={onFormInitialValuesChange} onExecutiveDelete={onExecutiveRemoval} />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
  onExecutiveDelete: React.MouseEventHandler;
}

const FormContent: FunctionComponent<FormContentProps> = ({ onFormInitialValuesChange, onExecutiveDelete }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, submitForm, initialValues, validateForm, dirty, isValid, setFieldError } =
    useFormikContext<ExecutiveInfoDataFormType>();
  const isSignatoryFormActive = useSelector(customerSelectors.selectIsSignatoryFormActive);
  const quoteStakeholderContact = useSelector(quoteSelectors.selectStakeholderContacts).find(
    (contact) => contact.milesStakeholderContactType === 'DIRECTOR'
  );
  const customerStakeholderContact = useSelector(customerSelectors.selectStakeholderContacts).DIRECTOR;
  const isQuoteStakeholderAvailable = useMemo(() => !!quoteStakeholderContact, [quoteStakeholderContact]);
  const isCustomerStakeholderContactAvailable = useMemo(
    () => !!customerStakeholderContact,
    [customerStakeholderContact]
  );
  const quoteId = useSelector(selectQuoteIdFromDataStorage);
  const dispatch = useDispatch();
  const isFormSubmitted = !!useSelector(creditApplicationSelectors.selectSubmittedForms).find(
    (form) => form === CustomerDetailsSection.EXECUTIVE_INFO
  );

  const [suggestionsForPlacefBirth, setSuggestionsForPlaceOfBirth] = useState<CitiesAndZipCodesSuggestion[]>([]);
  const [suggestedLocationsLoading, setSuggestedLocationsLoading] = useState(false);
  const [suggestionsForZipCodeOfBirth, setSuggestionsForZipCodeOfBirth] = useState<CitiesAndZipCodesSuggestion[]>([]);
  const [suggestedZipCodesLoading, setSuggestedZipCodesLoading] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [isStakeholderUnlinked, setIsStakeholderUnlinked] = useState(!isQuoteStakeholderAvailable);
  const [isEditMode, setIsEditMode] = useState(
    !isFormSubmitted && isStakeholderUnlinked && !isCustomerStakeholderContactAvailable
  );
  const [isUnlinking, setIsUnlinking] = useState(false);

  const isBornInFrance = values.ceoBirthCountry === CountryCodesEnum.FR;
  const areAllExecutiveFieldsDisabled = !isEditMode;
  const isContactDataEditable = isEditMode && isCustomerStakeholderContactAvailable;

  const isFrenchTerritory = frenchTerritoriesOptions.some((option) => option.value === values.ceoBirthCountry);

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    // When any form field changes after submit, user is forced to submit the form again
    if (dirty) {
      dispatch(setFormAsNotSubmitted(CustomerDetailsSection.EXECUTIVE_INFO));
    }
  }, [dirty, dispatch]);

  const onSelectSuggestion = (suggestion: string) => {
    const zipMatch = suggestion.match(/\b\d{5}\b/);
    const zip = zipMatch ? zipMatch[0] : null;
    const city = zip ? suggestion.replace(zip, '').trim() : suggestion.trim();

    setIsUserTyping(false);
    setFieldValue('ceoBirthPlaceCity', formatCityNameLettersAndSpaceOnly(city));

    if (isFrenchTerritory) {
      setFieldValue('ceoBirthPlaceZip', zip);
    }
  };

  const onZipCodeOfBirthChange = (value: string) => {
    setIsUserTyping(true);
    setFieldValue('ceoBirthPlaceZip', value);
  };

  const onPlaceOfBirthChange = (value: string) => {
    setIsUserTyping(true);
    setFieldValue('ceoBirthPlaceCity', value);
  };

  useEffect(() => {
    if (!values.ceoBirthPlaceZip || values.ceoBirthPlaceZip?.length < 5 || !isBornInFrance) return;

    async function fetchSuggestions() {
      try {
        setSuggestedZipCodesLoading(true);
        const res = await localitiesWithZipCodes(values.ceoBirthPlaceZip || '');
        setSuggestionsForZipCodeOfBirth(res);
        setSuggestionsForPlaceOfBirth([]);
      } catch {
        setSuggestedZipCodesLoading(false);
      } finally {
        setSuggestedZipCodesLoading(false);
      }
    }

    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.ceoBirthPlaceZip]);

  useEffect(() => {
    if (!values.ceoBirthPlaceCity || values.ceoBirthPlaceCity?.length < 3) return;

    async function fetchSuggestions() {
      try {
        setSuggestedLocationsLoading(true);
        const res = await zipCodesForLocality(values.ceoBirthPlaceCity || '');
        setSuggestionsForPlaceOfBirth(res);
        setSuggestionsForZipCodeOfBirth([]);
      } catch {
        setSuggestedLocationsLoading(false);
      } finally {
        setSuggestedLocationsLoading(false);
      }
    }

    const handler = setTimeout(() => {
      fetchSuggestions();
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(handler);
    };
  }, [values.ceoBirthPlaceCity]);

  const handleExecutiveStakeholderRemoval = async (event: any) => {
    await onExecutiveDelete(event);
    setIsEditMode(true);
    setShowDeletionModal(false);
  };

  const handleEditExecutiveStakeholder = async () => {
    if (!quoteId) return;

    try {
      if (!isStakeholderUnlinked) {
        setIsUnlinking(true);
        dispatch(loadingActions.setLoading(true));
        await unlinkStakeholderContact(
          assertDefined(quoteId),
          assertDefined(quoteStakeholderContact?.milesStakeholderContactId)
        );
        const updatedQuoteStakeholderContacts = await getQuoteStakeholderContacts(quoteId);
        dispatch(setStakeholderContacts(updatedQuoteStakeholderContacts.stakeholderContacts ?? []));
        setIsStakeholderUnlinked(true);
      }

      setIsEditMode(true);
      dispatch(setFormAsNotSubmitted(CustomerDetailsSection.EXECUTIVE_INFO));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsUnlinking(false);
      dispatch(loadingActions.setLoading(false));
    }
  };

  const handleExecutiveInfoSubmit = async () => {
    if (values.email) {
      const tlsValid = await validateEmailTls(values.email, setFieldError, dispatch);

      if (!tlsValid) {
        setIsEditMode(true);
        return;
      }
    }

    await submitForm();

    if (isValid) {
      setIsEditMode(false);
    }
  };

  return (
    <Fieldset className="u-pt-none">
      <Layout>
        <Layout.Item default="1/2">
          <Checkbox
            checked={isSignatoryFormActive}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              dispatch(setIsSignatoryFormActive(e.target.checked));
            }}
            disabled={!isEditMode}
          >
            <span> {t('step4:heading:signatoryFormToggle')} </span>
          </Checkbox>
        </Layout.Item>
        <Layout.Item default="1/2">
          <Layout className="u-float-right">
            <Layout.Item default="1/3">
              <Button
                secondary
                ariaLabel="delete director"
                type="button"
                disabled={!isStakeholderUnlinked || isUnlinking || !isCustomerStakeholderContactAvailable}
                icon="semantic-delete"
                onClick={() => setShowDeletionModal(true)}
              />
            </Layout.Item>
            <Layout.Item default="2/3">
              <Button
                secondary
                type="button"
                disabled={isUnlinking || isEditMode}
                onClick={handleEditExecutiveStakeholder}
              >
                {t('step4:stakeholderSection:buttons:edit')}
              </Button>
            </Layout.Item>
          </Layout>
        </Layout.Item>
      </Layout>
      <Fieldset.Row className="u-mt">
        <Layout>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <Gender isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable} t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoFirstName isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable} t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoLastName isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable} t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthName isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable} t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoDateOfBirth
              isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable}
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              label={t('step4:ceoDateOfBirth:label')}
              infoText={t('step4:dateInfoText:label')}
            />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoNationality
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable}
            />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthCountry
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable}
            />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthPlaceCity
              t={t}
              required={isBornInFrance}
              onChange={onPlaceOfBirthChange}
              onSelectSuggestion={onSelectSuggestion}
              isLoading={suggestedLocationsLoading}
              suggestions={isUserTyping ? suggestionsForPlacefBirth : []}
              isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable}
            />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthPlaceZip
              t={t}
              required={isBornInFrance}
              suggestions={isUserTyping ? suggestionsForZipCodeOfBirth : []}
              onChange={onZipCodeOfBirthChange}
              onSelectSuggestion={onSelectSuggestion}
              isLoading={suggestedZipCodesLoading}
              isDisabled={areAllExecutiveFieldsDisabled || isCustomerStakeholderContactAvailable || !isFrenchTerritory}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <CountryCodeMobilePhone
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={areAllExecutiveFieldsDisabled && !isContactDataEditable}
            />
          </Layout.Item>
          <Layout.Item default="2/5" m="1/2" s="1/1">
            <MobilePhone
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={areAllExecutiveFieldsDisabled && !isContactDataEditable}
              required={false}
            />
          </Layout.Item>
          <Layout.Item default="2/5" m="1/2" s="1/1">
            <Email t={t} isDisabled={areAllExecutiveFieldsDisabled && !isContactDataEditable} />
          </Layout.Item>
        </Layout>
        <ButtonContainer right className="u-mt-small">
          <Layout right>
            <Layout.Item default="1/3" m="2/5" s="2/3">
              <Button full type="submit" onClick={handleExecutiveInfoSubmit} disabled={!isEditMode || isUnlinking}>
                {t('step3:navigation:continue')}
              </Button>
            </Layout.Item>
          </Layout>
        </ButtonContainer>
      </Fieldset.Row>
      <StakeholderRemovalModal
        shown={showDeletionModal}
        onAbort={() => setShowDeletionModal(false)}
        onConfirm={handleExecutiveStakeholderRemoval}
      />
    </Fieldset>
  );
};
